import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { checkout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, Sparkles, Loader2 } from "lucide-react";

// Stripe webhook + local grant can take up to ~45s in sandbox. Poll every
// 2.5s for up to ~60s before showing the manual fallback.
const MAX_ATTEMPTS = 24;
const POLL_INTERVAL_MS = 2500;
// After confirming Pro, auto-continue to the app so users are never stranded.
const AUTO_CONTINUE_MS = 2500;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", message: "Confirming your payment..." });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No payment session found." });
      return;
    }
    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState({
          status: "pending",
          message: "Payment is still processing. We'll unlock your access as soon as Stripe confirms.",
        });
        return;
      }
      try {
        const r = await checkout.status(sessionId);
        if (r.payment_status === "paid") {
          await refresh();
          setState({
            status: "success",
            message: "You're all set — LifeGuide Pro is now active.",
            tier: r.tier,
          });
          return;
        }
        if (r.status === "expired") {
          setState({ status: "error", message: "This payment session expired. Please try again." });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  // Once we've confirmed Pro, auto-continue to Profile so users are never
  // stranded on a success screen.
  useEffect(() => {
    if (state.status !== "success") return;
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
      navigate("/profile", { replace: true });
    }, AUTO_CONTINUE_MS);
    return () => clearTimeout(t);
  }, [state.status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center fade-in-up">
        {state.status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Loader2 size={28} className="animate-spin" />
            </div>
            <h1 className="font-display text-2xl text-[#2E2E2E] mb-2" data-testid="payment-checking-title">
              {state.message}
            </h1>
            <p className="text-[#6B6B6B] text-sm leading-relaxed">
              This usually takes just a few seconds.
            </p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Check size={32} strokeWidth={2.2} />
            </div>
            <h1
              className="font-display text-3xl text-[#2E2E2E] mb-2"
              data-testid="payment-success-title"
            >
              You're all set
            </h1>
            <p className="text-[#6B6B6B] mb-8 leading-relaxed" data-testid="payment-success-message">
              {state.message}
            </p>
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/profile", { replace: true });
              }}
              data-testid="continue-to-lifeguide-btn"
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)] inline-flex items-center gap-2"
            >
              <Sparkles size={16} /> Continue to LifeGuide
            </button>
            <p className="mt-4 text-xs text-[#6B6B6B]">
              Taking you to your Profile automatically…
            </p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F5EBD9] text-[#8C7A4F] flex items-center justify-center">
              <Loader2 size={28} />
            </div>
            <h1 className="font-display text-2xl text-[#2E2E2E] mb-2">Almost there</h1>
            <p className="text-[#6B6B6B] mb-6 leading-relaxed">{state.message}</p>
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/profile");
              }}
              data-testid="back-to-profile-pending-btn"
              className="bg-white border border-[#DCE5DB] hover:bg-[#EEF3EC] text-[#2E2E2E] rounded-full px-6 py-3 font-medium transition"
            >
              Back to profile
            </button>
          </>
        )}
        {state.status === "error" && (
          <>
            <h1 className="font-display text-2xl text-[#2E2E2E] mb-2">Something went sideways</h1>
            <p className="text-[#6B6B6B] mb-6 leading-relaxed">{state.message}</p>
            <button
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/profile");
              }}
              data-testid="back-to-profile-error-btn"
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 font-medium transition"
            >
              Back to profile
            </button>
          </>
        )}
      </div>
    </div>
  );
}
